import React, { useCallback, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { deleteAccount, getCurrentPlatformTermsAndConditions, getCurrentProgramTermsAndConditionsByProgramId, getUserSettings } from "services";

import {
  getEnvironmentString,
  openCookiePreferences,
  openFileInNewTab,
  useCancelAsyncOnUnmount,
  useGetCarbonProgramById,
  useGetFarms,
  useNavigate,
  useTranslation,
  useUserContext,
} from "utils";

import { Col, ErrorBoundary, Icon, List, MainPage, Modal, Row, SelectLanguage, Typography, Message as message } from "components";

export const ProfileSettings = () => {
  const { t } = useTranslation(["user", "meta", "dashboard"]);
  const { user } = useUserContext();
  const { currentFarm } = useGetFarms();
  const navigate = useNavigate();
  const signal = useCancelAsyncOnUnmount()();

  const queryClient = useQueryClient();
  const programQuery = useGetCarbonProgramById(currentFarm?.carbonProgram?.id);

  const [loading, setLoading] = useState(true);
  const [shareData, setShareData] = useState(false);
  const [showDeleteModalConfirm, setShowDeleteModalConfirm] = useState(false);
  const [deleteProfilePassword, setDeleteProfilePassword] = useState<string>();

  const loadUserSettings = useCallback(() => {
    setLoading(true);

    getUserSettings({
      signal,
    })
      .then((res) => {
        setShareData(!!res?.dataShare);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal]);

  const handleDeleteAccount = () => {
    setLoading(true);

    if (!deleteProfilePassword) {
      message({ content: t("errors.mandatoryFields"), icon: "error" });
      return setLoading(false);
    }

    deleteAccount({ password: deleteProfilePassword }, signal)
      .then(() => {
        navigate("/sign-out");

        message({
          content: t("profileDelete.deleted"),
          icon: "tick",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePlatformTermsClick = async () => {
    const data = await getCurrentPlatformTermsAndConditions();
    openFileInNewTab(data);
  };

  const handleProgramTermsClick = async () => {
    if (currentFarm?.carbonProgram?.id) {
      const data = await getCurrentProgramTermsAndConditionsByProgramId(currentFarm.carbonProgram.id);
      openFileInNewTab(data);
    }
  };

  useEffect(() => {
    loadUserSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return null;

  const env = getEnvironmentString();

  return (
    <MainPage loading={loading} pageName={"profile"} containerClass="tiny">
      <Row className="padding-bottom-400">
        <Col>
          <Typography variant="h2">{t("settings.title")}</Typography>
        </Col>
      </Row>
      <ErrorBoundary>
        <div className="margin-bottom-250">
          <List
            items={[
              {
                title: t("notifications.title"),
                link: "/manage-notifications",
              },
            ]}
          />
        </div>
      </ErrorBoundary>
      <ErrorBoundary>
        <div className="margin-bottom-250">
          <List
            items={[
              {
                title: t("settings.edit.email"),
                description: user.email.toString(),
                link: "/user/change-email",
              },
              {
                title: t("settings.edit.password"),
                link: "/user/change-password",
              },
              {
                title: t("privacySettings.headline"),
                link: "/manage-privacy",
                description: shareData ? t("privacySettings.public") : t("privacySettings.private"),
              },
            ]}
          />
        </div>
      </ErrorBoundary>
      <ErrorBoundary>
        <List
          items={[
            {
              title: t("policies.platformTerms"),
              onClick: handlePlatformTermsClick,
            },
            ...(currentFarm?.carbonProgram?.id
              ? [
                  {
                    title: t("policies.programTerms", { name: programQuery.data?.programName }),
                    onClick: handleProgramTermsClick,
                  },
                ]
              : []),

            {
              title: t("policies.privacy"),
              link: "/privacy-policy",
            },
            {
              title: t("policies.cookie"),
              link: "/cookie-policy",
            },
            {
              title: t("policies.manageCookies"),
              onClick: openCookiePreferences,
            },
          ]}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <div className="margin-top-400">
          <SelectLanguage skipRefresh placement="bottomLeft" />
        </div>
      </ErrorBoundary>
      {env !== "production" ? (
        <ErrorBoundary>
          <Row className="margin-top-400">
            <Col>
              <Typography
                variant="paragraph-m"
                onClick={() => queryClient.invalidateQueries()}
                style={{ textDecoration: "none" }}
                color="green-400"
                className="link"
              >
                [Dev] Clear react-query cache
              </Typography>
            </Col>
          </Row>
        </ErrorBoundary>
      ) : null}
      <Row className="margin-top-400">
        <Col>
          <Typography variant="paragraph-m" onClick={() => setShowDeleteModalConfirm(true)} style={{ textDecoration: "none" }} color="red-500" className="link">
            <Icon iconName="tabler/trash" className="icon inline" /> {t("settings.delete")}
          </Typography>
        </Col>
      </Row>
      <Modal
        cancelText={t("profileDelete.cancel")}
        description={t("profileDelete.description")}
        input={{
          initialValue: "",
          name: "password",
          rules: [{ required: true }],
          label: t("profileDelete.password"),
          onChange: (v) => setDeleteProfilePassword(v.target.value),
        }}
        okText={t("profileDelete.confirm")}
        onCancel={() => setShowDeleteModalConfirm(false)}
        onOk={() => handleDeleteAccount()}
        title={t("profileDelete.title")}
        showModal={showDeleteModalConfirm}
      />
    </MainPage>
  );
};
